import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from 'devextreme-react/button';
import { TextBox } from 'devextreme-react/text-box';
import { CheckBox } from 'devextreme-react/check-box';
import { Toast } from 'devextreme-react/toast';
import { LoadPanel } from 'devextreme-react/load-panel';
import '../styles/LoginPage.css';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  
  // Password setup state
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [passwordSetupUser, setPasswordSetupUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const { login, checkPasswordStatus, setInitialPassword } = useAuth();
  const navigate = useNavigate();

  const handleCheckPasswordStatus = useCallback(async () => {
    if (!email) {
      setError('Prosim vnesite e-poštni naslov');
      setShowToast(true);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const response = await checkPasswordStatus(email);
      
      if (response.requiresPasswordSetup) {
        setPasswordSetupUser(response.user);
        setShowPasswordSetup(true);
      } else {
        setError('Uporabnik že ima nastavljeno geslo. Prosim vnesite geslo.');
        setShowToast(true);
      }
    } catch (err: any) {
      console.error('Check password status error:', err);
      
      if (err.response?.status === 400) {
        setError('Uporabnik s tem e-poštnim naslovom ne obstaja');
      } else {
        setError('Napaka pri preverjanju gesla. Poskusite znova.');
      }
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  }, [email, checkPasswordStatus]);

  const handleSubmit = useCallback(async () => {
    if (!email || !password) {
      setError('Prosim vnesite e-pošto in geslo');
      setShowToast(true);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const response = await login({
        email,
        password,
        rememberMe
      });
      
      // Check if user needs to set initial password
      if (response.requiresPasswordSetup) {
        setPasswordSetupUser(response.user);
        setShowPasswordSetup(true);
        setIsLoading(false); // Stop loading to show password setup form
        return;
      }
      
      navigate('/admin/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      
      if (err.response?.status === 400) {
        setError('Napačni podatki za prijavo');
      } else if (err.response?.status === 401) {
        setError('Napačen e-poštni naslov ali geslo');
      } else {
        setError('Napaka pri prijavi. Poskusite znova.');
      }
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  }, [email, password, rememberMe, login, navigate, setPasswordSetupUser, setShowPasswordSetup]);

  const handlePasswordSetup = useCallback(async () => {
    if (!newPassword || !confirmPassword) {
      setError('Prosim vnesite novo geslo in potrdite geslo');
      setShowToast(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Gesli se ne ujemata');
      setShowToast(true);
      return;
    }

    if (newPassword.length < 8) {
      setError('Geslo mora biti dolgo vsaj 8 znakov');
      setShowToast(true);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      await setInitialPassword({
        email: passwordSetupUser.email,
        newPassword,
        confirmPassword
      });
      
      navigate('/admin/dashboard');
    } catch (err: any) {
      console.error('Password setup error:', err);
      
      if (err.response?.status === 400) {
        setError(err.response.data.message || 'Napaka pri nastavitvi gesla');
      } else {
        setError('Napaka pri nastavitvi gesla. Poskusite znova.');
      }
      setShowToast(true);
    } finally {
      setIsLoading(false);
    }
  }, [newPassword, confirmPassword, passwordSetupUser, setInitialPassword, navigate]);

  return (
    <div className="login-viewport">
      <LoadPanel
        visible={isLoading}
        message={showPasswordSetup ? "Nastavljam geslo..." : "Prijavljam..."}
        showIndicator={true}
        shading={true}
      />
      
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h1 className="app-title">GraveSearch</h1>
            <p className="app-subtitle">Sistem za upravljanje pokopališč</p>
          </div>
          
          {!showPasswordSetup ? (
            <>
              <h2 className="login-title">Prijava za administratorje</h2>
              
              <form className="login-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <div className="input-group">
                  <label className="input-label">E-poštni naslov</label>
                  <TextBox
                    value={email}
                    onValueChanged={(e) => setEmail(e.value)}
                    mode="email"
                    placeholder="Vnesite e-poštni naslov"
                    height={48}
                    stylingMode="outlined"
                    width="100%"
                  />
                </div>
                
                <div className="input-group">
                  <label className="input-label">Geslo</label>
                  <TextBox
                    value={password}
                    onValueChanged={(e) => setPassword(e.value)}
                    mode="password"
                    placeholder="Vnesite geslo"
                    height={48}
                    stylingMode="outlined"
                    width="100%"
                  />
                </div>
                
                <div className="checkbox-group">
                  <CheckBox
                    value={rememberMe}
                    onValueChanged={(e) => setRememberMe(!!e.value)}
                    text="Zapomni si me"
                    iconSize={20}
                  />
                </div>
                
                <Button
                  text="Prijava"
                  type="default"
                  width="100%"
                  height={52}
                  disabled={isLoading}
                  stylingMode="contained"
                  useSubmitBehavior={true}
                />
              </form>

              <div className="login-divider">
                <span>ali</span>
              </div>

              <Button
                text="Imam račun, a nimam gesla"
                type="normal"
                width="100%"
                height={48}
                onClick={handleCheckPasswordStatus}
                disabled={isLoading || !email}
                stylingMode="outlined"
              />
{/* 
              <div className="login-help">
                <h3>Privzeti podatki:</h3>
                <p><strong>E-pošta:</strong> admin@gravesearch.com</p>
                <p><strong>Geslo:</strong> Admin123!</p>
              </div> */}
            </>
          ) : (
            <>
              <h2 className="login-title">Nastavite novo geslo</h2>
              <p className="password-setup-info">
                Pozdravljen/a <strong>{passwordSetupUser?.firstName} {passwordSetupUser?.lastName}</strong>!<br />
                Za prvi dostop morate nastaviti novo geslo.
              </p>
              
              <form className="login-form" onSubmit={(e) => { e.preventDefault(); handlePasswordSetup(); }}>
                <div className="input-group">
                  <label className="input-label">Novo geslo</label>
                  <TextBox
                    value={newPassword}
                    onValueChanged={(e) => setNewPassword(e.value)}
                    mode="password"
                    placeholder="Vnesite novo geslo (vsaj 8 znakov)"
                    height={48}
                    stylingMode="outlined"
                    width="100%"
                  />
                </div>
                
                <div className="input-group">
                  <label className="input-label">Potrdite geslo</label>
                  <TextBox
                    value={confirmPassword}
                    onValueChanged={(e) => setConfirmPassword(e.value)}
                    mode="password"
                    placeholder="Ponovno vnesite novo geslo"
                    height={48}
                    stylingMode="outlined"
                    width="100%"
                  />
                </div>
                
                <Button
                  text="Nastavi geslo"
                  type="default"
                  width="100%"
                  height={52}
                  disabled={isLoading}
                  stylingMode="contained"
                  useSubmitBehavior={true}
                />
              </form>

              <div className="password-requirements">
                <h3>Zahteve za geslo:</h3>
                <ul>
                  <li>Vsaj 8 znakov</li>
                  <li>Vsaj ena številka</li>
                  <li>Kombinacija črk in številk</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      <Toast
        visible={showToast}
        message={error}
        type="error"
        onHiding={() => setShowToast(false)}
        displayTime={4000}
        position="top center"
      />
    </div>
  );
};

export default LoginPage;